import * as React from "react";
import classes from "./SystemDetails.module.css";
import {
  getSystemDetails,
  deleteSystemDetails,
} from "services/centerPointUserServices";
import moment from "moment";
import BackdropComponent from "components/Spinner/Spinner";
import SnackBar from "components/SnackBar/SnackBar";
import SystemCredentials from "../SystemCredentials/SystemCredentials";
import { Button, Dialog, Tooltip, Typography } from "@mui/material";
import DeleteConfirmDialog from "../DeleteConfirmDialog/DeleteConfirmDialog";
import UTurnLeftIcon from "@mui/icons-material/UTurnLeft";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PrimeReactDataGrid from "components/PrimeReactDataGrid/PrimeReactDataGrid";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import { Switch } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Transition } from "components/Common/Header/Header";
import EmployeeDateOverrides from "../EmployeeDateOverrides/EmployeeDateOverride";
import { useAuth } from "context/AuthContext";

const SystemDetails = (props) => {
  const { hasPermission } = useAuth();
  const canDeleteLogonId = hasPermission("CP_Group");
  const [loader, setLoader] = React.useState(false);
  const [initialRows, setInitialRows] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [dataToBeEdited, setDataToBeEdited] = React.useState({});
  const [addOrEdit, setAddOrEdit] = React.useState("");
  const [isActive, setIsActive] = React.useState("Active");
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [rowData, setRowData] = React.useState([]);
  const [gridPageSize, setGridPageSize] = React.useState(10);
  const [cpEmployeeId, setCpEmployeeId] = React.useState(0);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [cancelRow, setCancelRow] = React.useState(false);
  const [snack, setSnack] = React.useState({
    open: false,
    message: "",
    severity: "",
  });
  const { handleSelectedOracleId, oracleOrExtEmpId, firstName, lastName } =
    props;

  const [employeeDates, setEmployeeDates] = React.useState({
    trainingDate: null,
    nestingDate: null,
    productionDate: null,
  });

  const handleToggle = (event) => {
    setIsActive(event.target.checked ? "Active" : "InActive");
  };

  const CustomIconButton = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    color: theme.palette.primary.main,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      color: theme.palette.primary.dark,
    },
    "& .MuiTypography-root": {
      marginRight: theme.spacing(1),
    },
  }));

  const IconButtonWithLabel = ({ onClick }) => (
    <CustomIconButton onClick={onClick}>
      <BorderColorIcon sx={{ fontSize: 30 }} />
    </CustomIconButton>
  );

  IconButtonWithLabel.propTypes = {
    onClick: PropTypes.func.isRequired,
  };

  const DeleteIconButton = ({ onClick, disabled }) => (
    <Box
      onClick={disabled ? undefined : onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        marginLeft: 1,
        color: disabled ? "action.disabled" : "error.main",
        cursor: disabled ? "not-allowed" : "pointer",
        "&:hover": {
          textDecoration: disabled ? "none" : "underline",
          color: disabled ? "action.disabled" : "error.dark",
        },
      }}
    >
      <DeleteOutlineOutlinedIcon variant="outlined" sx={{ fontSize: 30 }} />
    </Box>
  );

  DeleteIconButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
  };

  const renderWithTooltip = (field) => (params) => {
    const value = params?.[field];
    if (!value) return "";
    return (
      <Tooltip title={value} arrow placement="top">
        <span>{value}</span>
      </Tooltip>
    );
  };

  const columns = [
    {
      field: "type",
      header: "Type",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Type",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("type"),
    },
    {
      field: "systemName",
      header: "System",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by System",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("systemName"),
    },
    {
      field: "subSystem",
      header: "Sub System",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Sub System",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("subSystem"),
    },
    {
      field: "logonId",
      header: "Logon ID",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Logon ID",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("logonId"),
    },
    {
      field: "effectiveDate",
      header: "Effective Date ",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Effective Date",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("effectiveDate"),
    },
    {
      field: "expireDate",
      header: "Expire Date",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Expire Date",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("expireDate"),
    },
    {
      field: "employeeStatus",
      header: "Status",
      filter: true,
      sortable: true,
      filterPlaceholder: "Search by Status",
      bodyStyle: { textAlign: "left" },
      body: renderWithTooltip("employeeStatus"),
    },
    {
      field: "selection",
      header: "",
      sortable: false,
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <Tooltip title="Edit" arrow placement="top">
          <span>
            <IconButtonWithLabel
              onClick={() => {
                setAddOrEdit("Edit");
                setDataToBeEdited(params);
              }}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      field: "delete",
      header: "",
      sortable: false,
      bodyStyle: { textAlign: "center" },
      body: (params) => (
        <Tooltip
          title={
            canDeleteLogonId ? "Delete" : "You do not have permission to delete"
          }
          arrow
          placement="top"
        >
          <span>
            <DeleteIconButton
              disabled={!canDeleteLogonId}
              onClick={() => setDeleteRow(params)}
            />
          </span>
        </Tooltip>
      ),
    },
  ];

  const filterProps = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    systemName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    subSystem: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    logonId: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    effectiveDate: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    expireDate: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    employeeStatus: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
  };

  const calculatePageSize = () => {
    const height = window.innerHeight;
    const width = window.innerWidth;
    if (height > 1200) return 100;
    if (height > 900) return 50;
    if (height > 600) return 25;
    return 10;
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  React.useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        await fetchData();
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to fetch data:", error);
        }
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const data = initialRows?.filter(
      (rowItem) => rowItem?.employeeStatus === isActive,
    );

    setRows(data);
  }, [isActive]);

  const onRowSelectChange = (event) => {
    try {
      if (event.checked) {
        setSelectedRows(rowData);
      } else if (event.originalEvent.target.checked === false) {
        setSelectedRows([]);
      } else {
        setSelectedRows(event.value);
      }
    } catch (error) {
      setSnack({
        open: true,
        message: "Error occurred while changing row selection.",
        severity: "error",
      });
    }
  };

  const fetchData = async () => {
    setLoader(true);
    try {
      const response = await getSystemDetails(oracleOrExtEmpId);
      if (response?.data?.message === "Success") {
        const data = response?.data?.data?.map((rowItem, index) => ({
          id: index,
          OracleId: rowItem?.OracleId,
          CP_EmployeeId: rowItem?.CP_EmployeeId,
          type: rowItem?.Type,
          Location: rowItem?.Location,
          SubSystemId: rowItem?.SubSystemId,
          subSystem: rowItem["Sub System"],
          SystemId: rowItem?.SystemId,
          systemName: rowItem["System Name"],
          logonId: rowItem["Logon Id"],
          effectiveDate: rowItem["Effective Date"]
            ? moment.utc(rowItem["Effective Date"]).format("YYYY-MM-DD")
            : "",
          expireDate: rowItem["Expire Date"]
            ? moment.utc(rowItem["Expire Date"]).format("YYYY-MM-DD")
            : "",
          employeeStatus: rowItem?.EmployeeSystemStatus ? "Active" : "InActive",
          SystemTypeId: rowItem?.CP_SystemTypeId,
          GeoLocGLCdId: rowItem?.GeoLocGLCdId,
          EmployeeSystemId: rowItem?.CP_EmployeeSystemId,
        }));
        setRowData([]);
        setCpEmployeeId(data[0]?.CP_EmployeeId);
        setEmployeeDates({
          trainingDate: response?.data?.data[0]?.TrainingDateOverride
            ? moment
                .utc(response.data.data[0].TrainingDateOverride)
                .format("YYYY-MM-DD")
            : null,
          nestingDate: response?.data?.data[0]?.NestingDateOverride
            ? moment
                .utc(response.data.data[0].NestingDateOverride)
                .format("YYYY-MM-DD")
            : null,
          productionDate: response?.data?.data[0]?.ProductionStartDateOverride
            ? moment
                .utc(response.data.data[0].ProductionStartDateOverride)
                .format("YYYY-MM-DD")
            : null,
        });
        const filteredData = data?.filter(
          (item) => item?.employeeStatus === isActive,
        );
        const updatePageSize = () => {
          setGridPageSize(calculatePageSize());
        };
        updatePageSize();
        window.addEventListener("resize", updatePageSize);
        setInitialRows(data);
        setRows(filteredData);
        setRowData(filteredData);
      } else {
        setSnack({
          open: true,
          message: "Could not fetch the details of the employee",
          severity: "error",
        });
      }
      setLoader(false);
    } catch (error) {
      errorHandler(true, "Something went wrong !", "error");
    }
  };

  const errorHandler = (open, message, severity) => {
    setSnack({
      open: open,
      message: message,
      severity: severity,
    });
  };

  const handleCancelAddOrEdit = () => {
    setAddOrEdit("");
    setDataToBeEdited({});
  };

  const handleCancelDelete = () => {
    if (cancelRow) return;
    setDeleteRow(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteRow?.EmployeeSystemId) {
      errorHandler(true, "Unable to delete: record missing.", "error");
      setDeleteRow(null);
      return;
    }
    setCancelRow(true);
    try {
      const response = await deleteSystemDetails({
        EmployeeSystemId: deleteRow.EmployeeSystemId,
        LoggedOnUserName: localStorage.getItem("User"),
      });
      const result = response?.data?.data;
      const isSuccess =
        response?.data?.message === "Success" &&
        !(result?.IsError ?? result?.isError);

      if (isSuccess) {
        errorHandler(true, "Logon ID deleted successfully.", "success");
        setDeleteRow(null);
        await fetchData();
      } else {
        errorHandler(
          true,
          result?.ErrorMessage ||
            result?.errorMessage ||
            "This record could not be deleted. It may have already been removed.",
          "error",
        );
        setDeleteRow(null);
      }
    } catch (error) {
      errorHandler(
        true,
        "Something went wrong while deleting. Please try again.",
        "error",
      );
      setDeleteRow(null);
    }
    setCancelRow(false);
  };

  const handleAddSystem = () => {
    setAddOrEdit("Add");
  };

  return (
    <>
      {" "}
      {loader ? <BackdropComponent /> : null}
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <Button
            variant="contained"
            startIcon={
              <UTurnLeftIcon
                sx={{
                  transform: "rotate(90deg)",
                }}
              />
            }
            onClick={() => handleSelectedOracleId(false, null)}
            sx={{
              textTransform: "none",
              backgroundColor: "#3299ff",
              color: "white",
              borderRadius: "8px",
            }}
          >
            Return to Search Results
          </Button>
        </div>
        <Box
          display="flex"
          alignItems="center"
          sx={{
            marginTop: "30px",
          }}
        >
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: "bold",
              fontFamily: "urbanist",
              color: "#3F5765",
            }}
          >
            Employee
          </Typography>
          <ExpandMoreIcon sx={{ ml: 2 }} />
          <Divider
            sx={{
              flexGrow: 1,
              ml: 2,
              borderBottomWidth: 2,
            }}
          ></Divider>
        </Box>
        <div className={classes.employeeRow}>
          <div className={classes.details}>
            <span>{firstName}&nbsp;</span>
            <span>{lastName}&nbsp;</span> - <span>{oracleOrExtEmpId}</span>
          </div>

          <EmployeeDateOverrides
            cpEmployeeId={cpEmployeeId}
            initialDates={employeeDates}
            errorHandler={errorHandler}
          />
        </div>
        <div>
          <p className={classes.statusText}> Status :</p>
        </div>
        <div className={classes.buttonContainer}>
          <FormControlLabel
            control={
              <Switch
                sx={{ color: "#3299FF" }}
                checked={isActive === "Active"}
                onChange={handleToggle}
              />
            }
            label={isActive}
            sx={{
              fontFamily: "Urbanist",
              color: isActive === "InActive" ? "#3299FF" : "inherit",
            }}
            value={isActive ? "Active" : "InActive"}
          />
          <div className={classes.systemContainer}>
            {!addOrEdit && (
              <Button
                sx={{
                  textTransform: "none",
                  backgroundColor: "#3299ff",
                  color: "white",
                  marginLeft: "30px",
                }}
                variant="contained"
                buttonClass={classes.buttonClass}
                onClick={handleAddSystem}
              >
                Add System{" "}
              </Button>
            )}
          </div>
        </div>
        <Grid item xs={12} className={classes.primeReactGridContainer}>
          <Box
            sx={{
              width: "100%",
              overflow: "auto",
              marginBottom: "30px",
            }}
          >
            <PrimeReactDataGrid
              columns={columns}
              dataKey="id"
              filterprops={filterProps}
              globalFilterFields={[
                "type",
                "systemName",
                "subSystem",
                "logonId",
                "effectiveDate",
                "expireDate",
                "employeeStatus",
              ]}
              removableSort
              resizableColumns
              showGridlines
              paginator
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} record(s)"
              columnResizeMode="expand"
              value={rows}
              dragSelection
              metaKeySelection={false}
              rows={gridPageSize}
              onSelectionChange={onRowSelectChange}
              selection={selectedRows}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              selectionMode="single"
            />
          </Box>
        </Grid>
        <div>
          <Divider
            sx={{
              flexGrow: 1,
              ml: 3,
              borderBottomWidth: 2,
            }}
          ></Divider>
        </div>
        <Dialog
          open={addOrEdit !== ""}
          onClose={handleCancelAddOrEdit}
          maxWidth="lg"
          fullWidth
          TransitionComponent={Transition}
        >
          {addOrEdit && (
            <SystemCredentials
              addOrEdit={addOrEdit}
              handleCancelAddOrEdit={handleCancelAddOrEdit}
              employeeId={cpEmployeeId}
              fetchData={fetchData}
              dataToBeEdited={dataToBeEdited}
              errorHandler={errorHandler}
            />
          )}
        </Dialog>
        <DeleteConfirmDialog
          open={!!deleteRow}
          logonId={deleteRow?.logonId}
          loading={cancelRow}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      </div>
      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
    </>
  );
};

SystemDetails.propTypes = {
  handleSelectedOracleId: PropTypes.func.isRequired,
  oracleOrExtEmpId: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
};

export default SystemDetails;
